import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GridLegacy as Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Cake,
  Close,
  ContentCopy,
  Email,
  People,
  Phone,
  Place,
  Refresh,
  Search,
  Work,
} from '@mui/icons-material';
import { cvAPI, managerAPI } from '../../services/api';
import { EmptyState, Section, StatusChip, TableShell } from '../shared/ui';
import ResumeActions from '../shared/ResumeViewer';
import { tokens } from '../../theme';

const STAGES = [
  { value: 'all', label: 'All stages' },
  { value: 'cv_submitted', label: 'CV submitted' },
  { value: 'cv_review', label: 'CV review' },
  { value: 'interview_scheduled', label: 'Interview scheduled' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'final_review', label: 'Final review' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

const dash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

/**
 * Candidates put forward against the manager's requirements.
 *
 * The duplicate chip is the point of interest here: it opens the detail of who
 * submitted the person first and when, which is the question a reviewer
 * actually has when the same candidate arrives twice. The flag is a prompt, not
 * a verdict, so it can be confirmed or dismissed from that dialog.
 */
const RequirementCandidates = ({ requirement, onBack, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('all');
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [flagFor, setFlagFor] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { per_page: 100 };
      if (stage !== 'all') params.stage = stage;
      if (duplicatesOnly) params.duplicates_only = 'true';
      if (search.trim()) params.q = search.trim();

      const response = requirement?.id
        ? await managerAPI.getRequirementCandidates(requirement.id, params)
        : await managerAPI.getCandidates(params);
      setRows(response.data?.candidates || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load candidates.');
    } finally {
      setLoading(false);
    }
  }, [requirement, stage, duplicatesOnly, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const resolveFlag = async (action) => {
    if (!flagFor) return;
    setResolving(true);
    setError(null);
    try {
      await cvAPI.resolveDuplicate(flagFor.id, action, notes || undefined);
      setFlagFor(null);
      setNotes('');
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update the flag.');
    } finally {
      setResolving(false);
    }
  };

  const title = requirement
    ? `${requirement.role_title} · ${requirement.requirement_code || ''}`
    : 'All candidates';

  return (
    <>
      <Section
        title={`${title} — ${rows.length} candidate${rows.length === 1 ? '' : 's'}`}
        subtitle={
          requirement
            ? `${requirement.experience_band} · ${requirement.location} · ${requirement.headcount} opening${requirement.headcount === 1 ? '' : 's'}`
            : 'Everyone submitted across your requirements'
        }
        disablePadding
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {onBack && (
              <Button size="small" startIcon={<ArrowBack fontSize="small" />} onClick={onBack}>
                Back
              </Button>
            )}
            <TextField
              size="small"
              placeholder="Name, email, company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 210 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: tokens.color.inkTertiary }} />,
              }}
            />
            <FormControl size="small" sx={{ width: 170 }}>
              <Select value={stage} onChange={(e) => setStage(e.target.value)}>
                {STAGES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant={duplicatesOnly ? 'contained' : 'outlined'}
              color={duplicatesOnly ? 'warning' : 'inherit'}
              startIcon={<ContentCopy fontSize="small" />}
              onClick={() => setDuplicatesOnly((v) => !v)}
            >
              Duplicates
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={load} disabled={loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
      >
        {error && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="body2" sx={{ color: tokens.color.danger }}>{error}</Typography>
          </Box>
        )}

        {loading && rows.length === 0 ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<People />}
            title={duplicatesOnly ? 'No duplicates flagged' : 'No candidates yet'}
            description={
              duplicatesOnly
                ? 'Nothing here has been flagged as already submitted by another recruiter.'
                : 'Recruiters will appear here as they submit resumes against this requirement.'
            }
          />
        ) : (
          <TableShell sx={{ border: 'none' }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="center">Gender</TableCell>
                  <TableCell align="center">Age</TableCell>
                  <TableCell align="center">Experience</TableCell>
                  <TableCell>Current role</TableCell>
                  <TableCell>Submitted by</TableCell>
                  <TableCell align="center">Stage</TableCell>
                  <TableCell align="center">Flag</TableCell>
                  <TableCell align="center">Resume</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" noWrap>{c.name}</Typography>
                      {c.date_of_birth && (
                        <Typography variant="caption">
                          DOB {new Date(c.date_of_birth).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 210 }}>
                      <Typography variant="body2" noWrap>{c.email}</Typography>
                      <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
                        {dash(c.phone)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{dash(c.gender)}</TableCell>
                    <TableCell align="center">{dash(c.age)}</TableCell>
                    <TableCell align="center">
                      {c.total_experience_years != null ? `${c.total_experience_years} yrs` : '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>{dash(c.current_designation)}</Typography>
                      <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
                        {dash(c.current_company)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{dash(c.vendor_name)}</Typography>
                      {c.submitted_at && (
                        <Typography variant="caption">
                          {new Date(c.submitted_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center"><StatusChip status={c.current_stage} /></TableCell>
                    <TableCell align="center">
                      {c.is_duplicate ? (
                        <Tooltip title="Possible duplicate — click for detail">
                          <Chip
                            size="small"
                            icon={<ContentCopy sx={{ fontSize: 12 }} />}
                            label="Duplicate"
                            onClick={() => {
                              setFlagFor(c);
                              setNotes('');
                            }}
                            sx={{
                              bgcolor: tokens.color.warningSoft,
                              color: tokens.color.warning,
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: tokens.color.inkTertiary }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <ResumeActions candidateId={c.id} candidateName={c.name} variant="icons" />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setDetail(c)}>Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </Section>

      {/* -- Duplicate flag detail ------------------------------------------ */}
      <Dialog open={!!flagFor} onClose={resolving ? undefined : () => setFlagFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContentCopy fontSize="small" />
            <Typography sx={{ flex: 1, fontSize: '0.9375rem', fontWeight: 600 }}>
              Possible duplicate
            </Typography>
            <IconButton size="small" onClick={() => setFlagFor(null)} disabled={resolving}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {flagFor?.duplicate ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 600 }}>
                  {flagFor.name} may already be in the pipeline
                </AlertTitle>
                {flagFor.duplicate.reason}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                    First submitted by
                  </Typography>
                  <Typography variant="body1">{dash(flagFor.duplicate.original_submitted_by)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                    First submitted on
                  </Typography>
                  <Typography variant="body1">
                    {flagFor.duplicate.original_submitted_at
                      ? new Date(flagFor.duplicate.original_submitted_at).toLocaleString()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                    Matched on
                  </Typography>
                  <Typography variant="body1">
                    {String(flagFor.duplicate.match_type || '').replace(/_/g, ' ')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                    Confidence
                  </Typography>
                  <Typography variant="body1">
                    {flagFor.duplicate.match_score != null
                      ? `${Math.round(flagFor.duplicate.match_score)}%`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                    This submission
                  </Typography>
                  <Typography variant="body1">
                    {dash(flagFor.vendor_name)}
                    {flagFor.submitted_at ? ` on ${new Date(flagFor.submitted_at).toLocaleString()}` : ''}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Note (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                helperText="Recorded against the decision"
              />
              {flagFor.duplicate.reviewed && (
                <Typography variant="body2" sx={{ color: tokens.color.inkSecondary, mt: 1 }}>
                  Already reviewed
                  {flagFor.duplicate.reviewed_at
                    ? ` on ${new Date(flagFor.duplicate.reviewed_at).toLocaleString()}`
                    : ''}
                  .
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body1">No duplicate detail available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagFor(null)} disabled={resolving}>Close</Button>
          <Button
            variant="outlined"
            onClick={() => resolveFlag('dismiss')}
            disabled={resolving}
            startIcon={resolving ? <CircularProgress size={14} /> : null}
          >
            Not a duplicate
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => resolveFlag('confirm')}
            disabled={resolving}
          >
            Confirm duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- Candidate detail ----------------------------------------------- */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }} noWrap>
                {detail?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
                {detail?.current_designation || 'Candidate'}
                {detail?.current_company ? ` at ${detail.current_company}` : ''}
              </Typography>
            </Box>
            {detail?.is_duplicate && (
              <Chip
                size="small"
                label="Duplicate"
                sx={{ bgcolor: tokens.color.warningSoft, color: tokens.color.warning }}
              />
            )}
            <IconButton size="small" onClick={() => setDetail(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detail && (
            <Grid container spacing={2}>
              {[
                { icon: <Email fontSize="small" />, label: 'Email', value: detail.email },
                { icon: <Phone fontSize="small" />, label: 'Phone', value: dash(detail.phone) },
                { icon: null, label: 'Gender', value: dash(detail.gender) },
                {
                  icon: <Cake fontSize="small" />,
                  label: 'Date of birth / age',
                  value: detail.date_of_birth
                    ? `${new Date(detail.date_of_birth).toLocaleDateString()}${detail.age ? ` (${detail.age})` : ''}`
                    : dash(detail.age),
                },
                {
                  icon: <Work fontSize="small" />,
                  label: 'Total experience',
                  value: detail.total_experience_years != null ? `${detail.total_experience_years} years` : '—',
                },
                { icon: <Place fontSize="small" />, label: 'Current location', value: dash(detail.current_location) },
                { icon: null, label: 'Expected salary', value: dash(detail.expected_salary) },
                { icon: null, label: 'Stage', value: String(detail.current_stage || '').replace(/_/g, ' ') },
              ].map((item) => (
                <Grid item xs={12} sm={6} key={item.label}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary, display: 'block' }}>
                    {item.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {item.icon}
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
              {detail.address && (
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary, display: 'block' }}>
                    Address
                  </Typography>
                  <Typography variant="body1">{detail.address}</Typography>
                </Grid>
              )}
              {detail.skills && (
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary, display: 'block', mb: 0.5 }}>
                    Skills
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {String(detail.skills)
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((s) => (
                        <Chip key={s} size="small" variant="outlined" label={s} />
                      ))}
                  </Box>
                </Grid>
              )}
              {detail.notes && (
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ color: tokens.color.inkTertiary, display: 'block' }}>
                    Recruiter notes
                  </Typography>
                  <Typography variant="body1">{detail.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {detail ? (
            <ResumeActions candidateId={detail.id} candidateName={detail.name} />
          ) : <span />}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequirementCandidates;
